import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { captureException } from '@sentry/nextjs';
import { Controller, useForm } from 'react-hook-form';
import { CURRENCY_USD } from 'constants/currency';
import { useUpdateCoachPremiumCoachMutation } from 'types/generated/client';
import { convertFormattedPriceToUnitAmount } from 'utils/shared/money/convertFormattedPriceToUnitAmount';
import { convertUnitPriceToFormattedPrice } from 'utils/shared/money/convertUnitPriceToFormattedPrice';
import { getCurrencySymbol } from 'utils/shared/money/getCurrencySymbol';
import { useGetCurrentUser } from 'hooks/useGetCurrentUser';
import { useViewer } from 'hooks/useViewer';
import CloseIcon from 'svg/CloseIcon';
import Button, { ButtonText } from 'components/Button';
import Modal from 'components/modals/Modal';
import InputField from './TextInputField';
import { SetPriceFormValues, SetPriceModalProps } from './props';
import { defaultSetPriceValues, setPriceSchema } from './utils';

const SetPriceModal: React.FC<SetPriceModalProps> = ({
  open,
  handleClose,
  initialValues,
  onSuccess,
  currency,
}) => {
  const viewer = useViewer();
  const { user: currentUser } = useGetCurrentUser();
  const currencySymbol = getCurrencySymbol(
    currentUser?.stripeMerchantCurrency || currentUser?.currencyCoachDefault || CURRENCY_USD,
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<SetPriceFormValues>({
    resolver: zodResolver(setPriceSchema),
    defaultValues: defaultSetPriceValues,
  });

  const [updateCoachPremiumCoach, { loading: updateCoachPremiumCoachLoading }] =
    useUpdateCoachPremiumCoachMutation();

  const onSubmit = async (data: SetPriceFormValues) => {
    try {
      const res = await updateCoachPremiumCoach({
        variables: {
          premiumContentDescription: data.description,
          priceUnitAmountPremiumContentDefault: convertFormattedPriceToUnitAmount(
            data.price || 0,
            currency,
          ),
          coachId: viewer.userId,
        },
      });
      if (!res.errors?.length) {
        reset(defaultSetPriceValues);
        onSuccess();
        handleClose();
      }
    } catch (err) {
      captureException(err);
    }
  };

  useEffect(() => {
    initialValues &&
      reset({
        ...initialValues,
        price:
          convertUnitPriceToFormattedPrice(initialValues.price || 0, currency).priceFormatted ||
          undefined,
      });
  }, [initialValues]);

  return (
    <Modal isOpen={open} handleClose={handleClose} classNameMaxWidth="max-w-[35rem]">
      <div className="flex items-center justify-between border-b px-ds-xl py-ds-lg">
        <h2 className="typography-product-heading-desktop">Premium Content</h2>
        <ButtonText onClick={handleClose}>
          <CloseIcon className="h-5 w-5" />
        </ButtonText>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6 p-ds-xl">
        <Controller
          control={control}
          name="price"
          render={({ field }) => (
            <InputField
              id="price"
              label="Price per month"
              placeholder={`${currencySymbol}15`}
              type="number"
              error={errors.price?.message}
              {...field}
            />
          )}
        />
        <Controller
          control={control}
          name="description"
          render={({ field }) => (
            <InputField
              id="description"
              label="General description"
              placeholder="Description"
              type="textarea"
              error={errors.description?.message}
              {...field}
            />
          )}
        />
        <div className="flex justify-end">
          <Button
            type="submit"
            variant="primary"
            className="w-fit"
            size="lg"
            disabled={updateCoachPremiumCoachLoading}
          >
            Submit
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default SetPriceModal;
